import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plane, Hotel, TrendingUp, Cloud, Sprout, Wallet, Search, Filter, Trash2, Upload, Calendar } from "lucide-react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PartnerDashboard: React.FC = () => {
  const companyId = localStorage.getItem("partnerCompanyId") || "1";
  const companyName = localStorage.getItem("partnerCompanyName") || "Empresa";
  const companyType = localStorage.getItem("partnerCompanyType") || "aviation";
  const isAviation = companyType === "aviation";
  
  // Estado dos filtros
  const [filters, setFilters] = useState({
    periodo: "30",
    status: "todos",
    tipoBeneficio: "todos",
    dataInicial: "2025-12-17",
    dataFinal: "2026-01-16",
    busca: ""
  });

  // Função para calcular cashback baseado na emissão de CO2 (50 MSC por kg)
  const calcularCashback = (emissaoStr: string): number => {
    const emissaoNum = parseFloat(emissaoStr.replace(" kg", "").replace(",", ".").trim());
    if (isNaN(emissaoNum)) return 0;
    return emissaoNum * 50;
  };

  // Mock data - Em produção, viria de uma API com filtro WHERE empresa_id = companyId
  // Usando a mesma estrutura de dados da tela administrativa
  const clientsData: Record<string, any> = {
    "1": {
      id: "1",
      viagens: [
        { id: "1", data: "15/06/2024", rota: "GRU - SDU", distancia: "685 km", emissao: "0,185 kg" },
        { id: "2", data: "20/07/2024", rota: "SDU - GRU", distancia: "685 km", emissao: "0,420 kg" }
      ],
      checkIns: [
        { id: "1", data: "05/03/2024", origem: "Brasília, DF", transporte: "Aéreo Convencional", distancia: 1060, emissao: 265.0, moedas: 2650.0 },
        { id: "2", data: "18/03/2024", origem: "Curitiba, PR", transporte: "Carro", distancia: 410, emissao: 77.9, moedas: 779.0 },
        { id: "3", data: "28/03/2024", origem: "Porto Alegre, RS", transporte: "Ônibus", distancia: 1550, emissao: 77.5, moedas: 775.0 }
      ]
    }
  };

  // Mock de clientes ativos - Em produção viria de uma API
  const clients = [
    { id: "1", nomeCompleto: "João Silva", status: "Ativo" }
  ];

  // Calcular métricas agregadas usando a mesma lógica da tela administrativa
  const companyClientIds = ["1"]; // Em produção viria de uma API WHERE empresa_id = companyId
  
  let totalViagens = 0;
  let totalCheckIns = 0;
  let totalDistancia = 0;
  let totalCO2Kg = 0;
  let totalMSC = 0;

  companyClientIds.forEach(clientId => {
    const clientData = clientsData[clientId];
    if (clientData) {
      if (isAviation && clientData.viagens) {
        // Para aviação: contar viagens e somar distâncias/emissões
        totalViagens += clientData.viagens.length;
        clientData.viagens.forEach((viagem: any) => {
          const distanciaStr = viagem.distancia.replace(" km", "").trim();
          totalDistancia += parseFloat(distanciaStr) || 0;
          
          const emissaoStr = viagem.emissao.replace(" kg", "").replace(",", ".").trim();
          const emissaoKg = parseFloat(emissaoStr) || 0;
          totalCO2Kg += emissaoKg;
          totalMSC += calcularCashback(viagem.emissao);
        });
      } else if (!isAviation && clientData.checkIns) {
        // Para hotelaria: contar check-ins e somar distâncias/emissões
        totalCheckIns += clientData.checkIns.length;
        clientData.checkIns.forEach((checkIn: any) => {
          totalDistancia += checkIn.distancia || 0;
          totalCO2Kg += checkIn.emissao || 0;
          totalMSC += checkIn.moedas || 0;
        });
      }
    }
  });

  // Converter CO2 de kg para toneladas
  const totalCO2Toneladas = totalCO2Kg / 1000;

  // Calcular métricas finais (mesma lógica da tela administrativa)
  const dashboardMetrics = {
    clientesAtivos: clients.filter(c => c.status === "Ativo").length,
    totalViagens: isAviation ? totalViagens : totalCheckIns,
    totalDistancia: totalDistancia,
    totalCO2: totalCO2Toneladas, // em toneladas
    totalMudas: Math.ceil(totalCO2Toneladas / 0.15), // Baseado na taxa de 0.15 t CO2 por árvore
    totalMSC: totalMSC
  };

  // Dados para gráficos
  const meses = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  
  const clientesDataChart = {
    labels: meses.slice(0, 6),
    datasets: [
      {
        label: "Clientes Ativos",
        data: [1, 1, 1, 1, 2, 2],
        borderColor: "#0e7cb4",
        backgroundColor: "rgba(14, 124, 180, 0.1)",
        fill: true,
        tension: 0.4
      }
    ]
  };

  const operacoesData = {
    labels: meses.slice(0, 6),
    datasets: [
      {
        label: isAviation ? "Viagens" : "Check-ins",
        data: [0, 0, 2, 3, 2, isAviation ? 4 : 5],
        backgroundColor: "#0e7cb4"
      }
    ]
  };

  const mscData = {
    labels: meses.slice(0, 6),
    datasets: [
      {
        label: "Moeda Social Gerada (MSC)",
        data: [0, 0, 3000, 4500, 3000, totalMSC],
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.1)",
        fill: true,
        tension: 0.4
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top" as const
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  // 6 KPIs Cards (mesma ordem da tela administrativa)
  const kpiCards = [
    {
      title: "Clientes Ativos",
      value: dashboardMetrics.clientesAtivos.toString(),
      icon: Users,
      color: "text-[#0e7cb4]",
      bgColor: "bg-white",
      borderColor: "border-gray-200"
    },
    {
      title: isAviation ? "Viagens Realizadas" : "Check-ins Realizados",
      value: dashboardMetrics.totalViagens.toString(),
      icon: isAviation ? Plane : Hotel,
      color: "text-[#0e7cb4]",
      bgColor: "bg-white",
      borderColor: "border-gray-200"
    },
    {
      title: "Distância Total",
      value: `${dashboardMetrics.totalDistancia.toLocaleString('pt-BR')} km`,
      icon: TrendingUp,
      color: "text-[#0e7cb4]",
      bgColor: "bg-white",
      borderColor: "border-gray-200"
    },
    {
      title: "Emissões (CO₂e)",
      value: `${dashboardMetrics.totalCO2.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} t`,
      icon: Cloud,
      color: "text-[#0e7cb4]",
      bgColor: "bg-white",
      borderColor: "border-gray-200"
    },
    {
      title: "Mudas Financiadas",
      value: `${dashboardMetrics.totalMudas}`,
      icon: Sprout,
      color: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200"
    },
    {
      title: "Moeda Social Gerada (MSC)",
      value: `${dashboardMetrics.totalMSC.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MSC`,
      icon: Wallet,
      color: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200"
    }
  ];

  const handleApplyFilters = () => {
    // Em produção, faria uma chamada à API com os filtros
    console.log("Aplicar filtros:", filters);
  };

  const handleClearFilters = () => {
    setFilters({
      periodo: "30",
      status: "todos",
      tipoBeneficio: "todos",
      dataInicial: "",
      dataFinal: "",
      busca: ""
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Visão geral do impacto da {companyName}</p>
      </div>

      {/* 6 KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpiCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <Card key={index} className={`${card.bgColor} ${card.borderColor} border`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {card.bgColor.includes("green") && <Icon className={`h-4 w-4 ${card.color}`} />}
                      <div className={`text-sm ${card.bgColor.includes("green") ? "text-muted-foreground" : "text-muted-foreground"}`}>
                        {card.title}
                      </div>
                    </div>
                    <div className={`text-3xl font-bold ${card.color}`}>
                      {card.value}
                    </div>
                  </div>
                  {!card.bgColor.includes("green") && (
                    <Icon className={`h-8 w-8 ${card.color} opacity-50`} />
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtros de Pesquisa */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Filtros de Pesquisa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Período</Label>
              <Select value={filters.periodo} onValueChange={(value) => setFilters({...filters, periodo: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Últimos 7 dias</SelectItem>
                  <SelectItem value="30">Últimos 30 dias</SelectItem>
                  <SelectItem value="90">Últimos 90 dias</SelectItem>
                  <SelectItem value="365">Último ano</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os status</SelectItem>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tipo de Benefício</Label>
              <Select value={filters.tipoBeneficio} onValueChange={(value) => setFilters({...filters, tipoBeneficio: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os benefícios</SelectItem>
                  <SelectItem value="desconto">Desconto</SelectItem>
                  <SelectItem value="cashback">Cashback</SelectItem>
                  <SelectItem value="pontos">Pontos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Data Inicial</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="date"
                  value={filters.dataInicial}
                  onChange={(e) => setFilters({...filters, dataInicial: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Data Final</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="date"
                  value={filters.dataFinal}
                  onChange={(e) => setFilters({...filters, dataFinal: e.target.value})}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Buscar por Nome/CPF</Label>
              <Input
                placeholder="Digite nome ou CPF"
                value={filters.busca}
                onChange={(e) => setFilters({...filters, busca: e.target.value})}
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button onClick={handleApplyFilters} className="bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]">
              <Filter className="mr-2 h-4 w-4" />
              Aplicar Filtros
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              <Trash2 className="mr-2 h-4 w-4" />
              Limpar Filtros
            </Button>
            <Button variant="outline">
              <Upload className="mr-2 h-4 w-4" />
              Exportar Resultados
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Evolução de Clientes Ativos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Line data={clientesDataChart} options={chartOptions} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Volume de Operações ({isAviation ? "Viagens" : "Check-ins"})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar data={operacoesData} options={chartOptions} />
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Geração de Moeda Social (MSC) ao Longo do Tempo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Line data={mscData} options={chartOptions} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PartnerDashboard;
